import { useState, useRef, useMemo, useCallback, type ReactNode } from "react"
import {
  differenceInDays,
  eachMonthOfInterval,
  format,
  startOfMonth,
  endOfMonth,
  isToday,
  min as minDate,
  max as maxDate,
  subDays,
  addDays,
} from "date-fns"
import { Button } from "@/components/ui/button"
import { ChevronRight, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

export interface GanttRow {
  id: string | number
  label: string
  sublabel?: string
  startDate: Date
  endDate: Date
  color: string
  tooltip?: ReactNode
  children?: GanttRow[]
}

interface GanttChartProps {
  rows: GanttRow[]
  expandable?: boolean
}

type ZoomLevel = "month" | "quarter" | "year"

const PIXELS_PER_DAY: Record<ZoomLevel, number> = {
  month: 8,
  quarter: 3,
  year: 1.5,
}

const ROW_HEIGHT = 40
const CHILD_ROW_HEIGHT = 32
const LABEL_WIDTH = 220

export function GanttChart({ rows, expandable = false }: GanttChartProps) {
  const [zoom, setZoom] = useState<ZoomLevel>("month")
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [hovered, setHovered] = useState<{ row: GanttRow; x: number; y: number } | null>(null)
  const chartRef = useRef<HTMLDivElement>(null)

  const toggleExpand = useCallback((id: string | number) => {
    const key = String(id)
    setExpanded(prev => {
      const next = { ...prev }
      next[key] = !next[key]
      return next
    })
  }, [])

  // Compute date range from all rows (including children)
  const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
    const allDates: Date[] = []
    const collectDates = (items: GanttRow[]) => {
      for (const row of items) {
        if (row.startDate) allDates.push(row.startDate)
        if (row.endDate) allDates.push(row.endDate)
        if (row.children) collectDates(row.children)
      }
    }
    collectDates(rows)

    if (allDates.length === 0) {
      const now = new Date()
      return { rangeStart: subDays(now, 30), rangeEnd: addDays(now, 30), totalDays: 60 }
    }

    const earliest = startOfMonth(subDays(minDate(allDates), 7))
    const latest = endOfMonth(addDays(maxDate(allDates), 7))
    return {
      rangeStart: earliest,
      rangeEnd: latest,
      totalDays: differenceInDays(latest, earliest) || 1,
    }
  }, [rows])

  // Month labels for the header
  const months = useMemo(() => {
    return eachMonthOfInterval({ start: rangeStart, end: rangeEnd })
  }, [rangeStart, rangeEnd])

  const pxPerDay = PIXELS_PER_DAY[zoom]
  const timelineWidth = totalDays * pxPerDay

  // Today marker position
  const today = new Date()
  const todayOffset = differenceInDays(today, rangeStart)
  const todayLeft = (todayOffset / totalDays) * 100

  // Flatten rows for rendering (with expand logic)
  const flatRows = useMemo(() => {
    const result: { key: string; row: GanttRow; depth: number; isChild: boolean }[] = []
    for (const row of rows) {
      result.push({ key: `parent-${row.id}`, row, depth: 0, isChild: false })
      if (expandable && row.children && expanded[String(row.id)]) {
        for (let i = 0; i < row.children.length; i++) {
          result.push({ key: `child-${row.id}-${i}`, row: row.children[i], depth: 1, isChild: true })
        }
      }
    }
    return result
  }, [rows, expanded, expandable])

  const getBarStyle = (row: GanttRow) => {
    const startOffset = differenceInDays(row.startDate, rangeStart)
    const duration = Math.max(differenceInDays(row.endDate, row.startDate), 1)
    return {
      left: `${(startOffset / totalDays) * 100}%`,
      width: `${(duration / totalDays) * 100}%`,
    }
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 text-sm">
        No data to display on the Gantt chart.
      </div>
    )
  }

  return (
    <div ref={chartRef} className="relative flex flex-col h-full bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      {/* Zoom controls */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
        <span className="text-xs text-slate-500 mr-2">Zoom:</span>
        {(["month", "quarter", "year"] as ZoomLevel[]).map((level) => (
          <Button
            key={level}
            variant="outline"
            size="sm"
            className={cn(
              "h-7 px-3 text-xs capitalize",
              zoom === level && "bg-slate-700 text-white hover:bg-slate-700"
            )}
            onClick={() => setZoom(level)}
            aria-pressed={zoom === level}
          >
            {level}
          </Button>
        ))}
      </div>

      {/* Chart area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Fixed label column */}
        <div className="flex-shrink-0 border-r border-slate-200 bg-white z-10" style={{ width: LABEL_WIDTH }}>
          {/* Header spacer */}
          <div className="h-10 border-b border-slate-200 bg-slate-700 px-3 flex items-center">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-white">Name</span>
          </div>
          {/* Row labels */}
          {flatRows.map(({ key, row, depth, isChild }) => (
            <div
              key={key}
              className={cn(
                "flex items-center px-3 border-b border-slate-100 hover:bg-slate-50 transition-colors",
                isChild ? "bg-slate-50/50" : ""
              )}
              style={{
                height: isChild ? CHILD_ROW_HEIGHT : ROW_HEIGHT,
                paddingLeft: depth * 20 + 12,
              }}
            >
              {expandable && !isChild && row.children && row.children.length > 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleExpand(row.id); }}
                  aria-label={`${expanded[String(row.id)] ? "Collapse" : "Expand"} ${row.label}`}
                  aria-expanded={!!expanded[String(row.id)]}
                  className="mr-1.5 text-slate-400 hover:text-slate-700 p-0.5 rounded hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                >
                  {expanded[String(row.id)]
                    ? <ChevronDown className="w-3.5 h-3.5" />
                    : <ChevronRight className="w-3.5 h-3.5" />
                  }
                </button>
              )}
              <div className="min-w-0 flex-1">
                <div className={cn("text-xs truncate", isChild ? "text-slate-600" : "text-slate-800 font-medium")}>
                  {row.label}
                </div>
                {row.sublabel && (
                  <div className="text-[10px] text-slate-400 truncate">{row.sublabel}</div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Scrollable timeline area */}
        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div style={{ width: timelineWidth, minWidth: "100%" }}>
            {/* Month header */}
            <div className="h-10 border-b border-slate-200 bg-slate-700 flex relative">
              {months.map((month, i) => {
                const monthStart = differenceInDays(month, rangeStart)
                const monthEnd = differenceInDays(
                  i < months.length - 1 ? months[i + 1] : rangeEnd,
                  rangeStart
                )
                const left = (monthStart / totalDays) * 100
                const width = ((monthEnd - monthStart) / totalDays) * 100
                return (
                  <div
                    key={month.toISOString()}
                    className="absolute top-0 h-full flex items-center justify-center border-r border-slate-600 text-[11px] font-medium text-white uppercase tracking-wider"
                    style={{ left: `${left}%`, width: `${width}%` }}
                  >
                    {format(month, "MMM yyyy")}
                  </div>
                )
              })}
            </div>

            {/* Rows with bars */}
            <div className="relative">
              {/* Today marker */}
              {todayOffset >= 0 && todayOffset <= totalDays && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-red-400 z-10 pointer-events-none"
                  style={{ left: `${todayLeft}%` }}
                >
                  <div className="absolute -top-0 -left-[22px] bg-red-400 text-white text-[9px] px-1.5 py-0.5 rounded-b font-medium">
                    Today
                  </div>
                </div>
              )}

              {/* Month grid lines */}
              {months.map((month) => {
                const offset = (differenceInDays(month, rangeStart) / totalDays) * 100
                return (
                  <div
                    key={`grid-${month.toISOString()}`}
                    className="absolute top-0 bottom-0 w-px bg-slate-100 pointer-events-none"
                    style={{ left: `${offset}%` }}
                  />
                )
              })}

              {flatRows.map(({ key, row, depth, isChild }) => {
                const barStyle = getBarStyle(row)
                const height = isChild ? CHILD_ROW_HEIGHT : ROW_HEIGHT
                const barHeight = isChild ? 16 : 24

                return (
                  <div
                    key={key}
                    className={cn(
                      "relative border-b border-slate-100 hover:bg-slate-50/50 transition-colors",
                      isChild && "bg-slate-50/30"
                    )}
                    style={{ height }}
                  >
                    <button
                      type="button"
                      aria-label={`${row.label}: ${format(row.startDate, "MMM d, yyyy")} to ${format(row.endDate, "MMM d, yyyy")}`}
                      className={cn(
                        "absolute rounded-full cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-1",
                        row.color
                      )}
                      style={{
                        ...barStyle,
                        height: barHeight,
                        top: (height - barHeight) / 2,
                      }}
                      onMouseEnter={(e) => {
                        const rect = chartRef.current?.getBoundingClientRect()
                        if (rect) setHovered({ row, x: e.clientX - rect.left, y: e.clientY - rect.top })
                      }}
                      onMouseMove={(e) => {
                        const rect = chartRef.current?.getBoundingClientRect()
                        if (rect) setHovered({ row, x: e.clientX - rect.left, y: e.clientY - rect.top })
                      }}
                      onMouseLeave={() => setHovered(null)}
                      onFocus={(e) => {
                        const rect = chartRef.current?.getBoundingClientRect()
                        const barRect = e.currentTarget.getBoundingClientRect()
                        if (rect) setHovered({ row, x: barRect.left - rect.left + barRect.width / 2, y: barRect.top - rect.top })
                      }}
                      onBlur={() => setHovered(null)}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Cursor-following tooltip */}
      {hovered && (
        <div
          className="pointer-events-none absolute z-50 rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md"
          style={{ left: hovered.x + 12, top: hovered.y - 8 }}
        >
          {hovered.row.tooltip ?? (
            <div>
              <div className="font-medium">{hovered.row.label}</div>
              {hovered.row.sublabel && <div className="text-muted-foreground">{hovered.row.sublabel}</div>}
              <div className="text-muted-foreground mt-1">
                {format(hovered.row.startDate, "MMM d, yyyy")} — {format(hovered.row.endDate, "MMM d, yyyy")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
